import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { getSportWithConfig } from "../../services/api/sportsService";
import { apiClient } from "../../services/api/apiService";
import GameLayout from "../layout/GameLayout";
import FloatingCard from "../ui/FloatingCard";
import UserSearchInput from "./UserSearchInput";
import TeamSearchInput from "./TeamSearchInput";
import VenueSearch from "../ui/VenueSearch";
import { getCurrentSportOptions, getSportDefaults, getSportConfigKey, isTeamSport as checkIsTeamSport } from "../../config/sportsConfig";
import { eid } from "../../utils/entityId";
import { isMatchConfigCustomized, canForceCreateMatch, localDateTimeInputValue } from "../../utils/matchFormUtils";
import "../../styles/features/matches.css";
import { eventTimeZone, formatEventDateTime, viewerTimeZone } from "../../utils/eventTime";

const MatchCreation = ({ token, user }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Helpers
  // Default to now + 5 minutes (minimum allowed schedule buffer)
  const defaultScheduledAt = () => localDateTimeInputValue();

  // Form data
  const [formData, setFormData] = useState({
    match_name: "",
    tournament_id: "",
    league_id: "",
    match_category: "friendly",
    sport_id: "",
    participant_1_id: "",
    participant_2_id: "",
    participant_1_type: "user", // "user" or "team"
    participant_2_type: "user",
    // Badminton doubles specific fields
    team_a_player_1_id: "",
    team_a_player_2_id: "",
    team_b_player_1_id: "",
    team_b_player_2_id: "",
    scheduled_at: defaultScheduledAt(),
    venue: "",
    venue_place_id: "",
    venue_address: "",
    venue_lat: null,
    venue_lng: null,
    court: "",
    notes: "",
    is_private: false,
    skill_level: "beginner",
    _team2_name: "", // display label for the TeamSearchInput picker (not sent to API)
  });

  // Options for dropdowns
  const [tournaments, setTournaments] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [contextData, setContextData] = useState({ leagues: [], tournaments: [] });
  const [sports, setSports] = useState([]);
  const [teams, setTeams] = useState([]);
  const [selectedSport, setSelectedSport] = useState(null);
  const [sportConfig, setSportConfig] = useState(null);
  const [isTeamSport, setIsTeamSport] = useState(false);
  const [isDoublesMatch, setIsDoublesMatch] = useState(false);
  // Badminton and table tennis can be played as singles or doubles
  const [isDoublesCapableSport, setIsDoublesCapableSport] = useState(false);
  const [badmintonMatchType, setBadmintonMatchType] = useState('singles');
  const [sportLockedBy, setSportLockedBy] = useState(null); // null | 'tournament' | 'league'
  const [entityTeams, setEntityTeams] = useState(null); // null = no entity constraint; array = entity-scoped teams

  // Schedule conflict state
  const [conflictData, setConflictData] = useState(null); // 409 conflict payload
  const [pendingPayload, setPendingPayload] = useState(null); // stored for force-create

  // Category "Coming Soon" modal state (Training / Ranked are upcoming features)
  const [showCategoryComingSoon, setShowCategoryComingSoon] = useState(false);

  // Match configuration customization state
  const [matchConfig, setMatchConfig] = useState({
    points_to_win: null,
    win_by_margin: null,
    max_points: null,
    total_games: null
  });
  // The selected sport's own default format, to tell whether the host customised it
  const [defaultMatchConfig, setDefaultMatchConfig] = useState(null);
  const applyDefaultMatchConfig = (cfg) => {
    setMatchConfig(cfg);
    setDefaultMatchConfig(cfg);
  };

  // Parse a <select> value: '' / 'null' → null, otherwise an integer.
  // 0 is a valid choice (e.g. "No Margin") and must not become null.
  const parseConfigValue = (raw) => {
    if (raw === '' || raw === 'null' || raw === null || raw === undefined) return null;
    const n = parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  };

  /**
   * Initial match format for a sport, taken from the sport's own config
   * (sportsConfig.js) — never another sport's numbers. Optional catalog
   * scoring rules override individual values when present.
   */
  const buildDefaultMatchConfig = (sportName, catalogRules = {}) => {
    const d = getSportDefaults(sportName);
    const key = getSportConfigKey(sportName);
    const pick = (...vals) => vals.find((v) => v !== undefined && v !== null);
    const cfg = {
      points_to_win: pick(catalogRules.points_to_win, d.points_to_win, null),
      win_by_margin: pick(catalogRules.win_by_margin, d.win_by_margin, null),
      max_points: pick(catalogRules.max_points, d.max_points, null),
      total_games: pick(catalogRules.total_games, d.total_games, 1),
      games_to_win: pick(catalogRules.games_to_win, d.games_to_win, null),
    };
    if (key === 'volleyball' || key === 'tabletennis') cfg.max_points = 999; // no cap (FIVB / ITTF)
    if (key === 'basketball') {
      cfg.total_games = 1;
      cfg.periods = pick(catalogRules.periods, d.periods, 4);
      cfg.period_minutes = pick(catalogRules.period_minutes, d.period_minutes, 10);
      cfg.overtime_minutes = pick(catalogRules.overtime_minutes, d.overtime_minutes, 5);
    }
    return cfg;
  };

  // Selected participants for user search components
  const [selectedParticipant1, setSelectedParticipant1] = useState(null);
  const [selectedParticipant2, setSelectedParticipant2] = useState(null);

  // Badminton doubles selected players
  const [selectedPlayerA1, setSelectedPlayerA1] = useState(null);
  const [selectedPlayerA2, setSelectedPlayerA2] = useState(null);
  const [selectedPlayerB1, setSelectedPlayerB1] = useState(null);
  const [selectedPlayerB2, setSelectedPlayerB2] = useState(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  // Helper function to get current sport configuration options
  const getCurrentSportOptionsLocal = () => {
    const sportName = selectedSport?.name?.toLowerCase() || '';
    return getCurrentSportOptions(sportName);
  };

  // Display name used in the singles/doubles UI
  const doublesSportLabel = getSportConfigKey(selectedSport?.name) === 'tabletennis' ? 'Table Tennis' : 'Badminton';

  /**
   * Apply sport selection side-effects by DB sport id.
   * Called both from the sport dropdown and from tournament/league auto-select.
   */
  const applySelectedSport = (sportId) => {
    const sport = sports.find(s => s.id === sportId || s.id === parseInt(sportId));
    if (!sport) return;

    const sportName = sport.name?.toLowerCase() || '';
    const isTeam = checkIsTeamSport(sportName);

    let catalogId = null;
    if (sportName.includes('table tennis') || sportName.includes('table_tennis')) catalogId = 'table_tennis_singles';
    else if (sportName.includes('badminton')) catalogId = 'badminton_singles';
    else if (sportName.includes('volleyball')) catalogId = 'volleyball_6v6';
    else if (sportName.includes('basketball')) catalogId = 'basketball_5v5';
    else if (sportName.includes('football') && !sportName.includes('american')) catalogId = 'football_11v11';
    else if (sportName.includes('tennis')) catalogId = 'tennis_singles';
    else if (sportName.includes('handball')) catalogId = 'handball_7v7';
    else if (sportName.includes('field hockey')) catalogId = 'field_hockey_11v11';
    else if (sportName.includes('ice hockey')) catalogId = 'ice_hockey_6v6';
    else if (sportName.includes('american football')) catalogId = 'american_football_11v11';
    else if (sportName.includes('baseball')) catalogId = 'baseball_9v9';
    else if (sportName.includes('athletics')) catalogId = 'athletics_individual';
    else if (sportName.includes('cricket')) catalogId = 'cricket_11v11';
    else if (sportName.includes('rugby')) catalogId = 'rugby_15v15';
    else if (sportName.includes('netball')) catalogId = 'netball_7v7';
    else if (sportName.includes('swimming')) catalogId = 'swimming_individual';
    else catalogId = sport.sport_id;

    const sportKey = getSportConfigKey(sportName);
    setSelectedSport(sport);
    setIsDoublesCapableSport(sportKey === 'badminton' || sportKey === 'tabletennis');
    setBadmintonMatchType('singles');
    setIsTeamSport(isTeam);
    setIsDoublesMatch(false);
    applyDefaultMatchConfig(buildDefaultMatchConfig(sportName));

    if (catalogId) fetchSportConfigFromCatalog(catalogId);
    else fetchSportConfig(sport.id);

    setFormData(prev => ({
      ...prev,
      sport_id: String(sport.id),
      participant_1_id: '',
      participant_2_id: '',
      team_a_player_1_id: '',
      team_a_player_2_id: '',
      team_b_player_1_id: '',
      team_b_player_2_id: '',
      participant_1_type: isTeam ? 'team' : 'user',
      participant_2_type: isTeam ? 'team' : 'user',
    }));
  };

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Single endpoint returns everything scoped to the current user:
      // leagues, tournaments, teams, and relevant sports.
      const ctxResponse = await apiClient.get('/matches/creation-context').catch((e) => {
        console.warn('Failed to load creation context:', e);
        return null;
      });

      const ctx = ctxResponse?.data?.data || {
        leagues: [], tournaments: [], teams: [], sports: [],
      };
      setContextData(ctx);
      setLeagues(Array.isArray(ctx.leagues) ? ctx.leagues : []);
      setTournaments(Array.isArray(ctx.tournaments) ? ctx.tournaments : []);
      setTeams(Array.isArray(ctx.teams) ? ctx.teams : []);
      setSports(Array.isArray(ctx.sports) ? ctx.sports : []);
    } catch (err) {
      setError('Failed to load initial data: ' + err.message);
      console.error('Error loading initial data:', err);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch teams scoped to a specific league or tournament and set entityTeams.
   * Called whenever the league/tournament dropdown changes.
   */
  const loadEntityTeams = async (entityType, entityId) => {
    if (!entityId) {
      setEntityTeams(null);
      return;
    }
    try {
      if (entityType === 'league') {
        const res = await apiClient.get(`/leagues/${entityId}/teams`);
        const rows = res.data?.data || res.data || [];
        setEntityTeams(
          rows.map((r) => ({ id: r.team_id, name: r.team_name }))
        );
      } else if (entityType === 'tournament') {
        const res = await apiClient.get(`/tournaments/${entityId}/registrations`, {
          params: { status: 'approved' },
        });
        const rows = res.data?.data || res.data || [];
        // De-duplicate by team_id (one row per team)
        const seen = new Set();
        const unique = [];
        for (const r of rows) {
          if (r.team_id && !seen.has(r.team_id)) {
            seen.add(r.team_id);
            unique.push({ id: r.team_id, name: r.team_name });
          }
        }
        setEntityTeams(unique);
      }
    } catch (err) {
      console.warn('Failed to load entity teams:', err);
      setEntityTeams(null);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      // Mutual exclusion: selecting a tournament clears league and vice versa
      if (name === 'tournament_id' && value) next.league_id = '';
      if (name === 'league_id' && value) next.tournament_id = '';
      // Tournament / league matches must always be public
      if ((name === 'tournament_id' || name === 'league_id') && value) {
        next.is_private = false;
      }
      // When Team 1 changes, clear Team 2 so it resets
      if (name === 'participant_1_id') {
        next.participant_2_id = '';
        next._team2_name = '';
      }
      return next;
    });

    // Auto-select sport from tournament
    if (name === 'tournament_id') {
      if (value) {
        const t = contextData.tournaments.find(x => String(x.id) === String(value));
        if (t?.sport_id) {
          applySelectedSport(t.sport_id);
          setSportLockedBy('tournament');
        } else {
          setSportLockedBy(null);
        }
        loadEntityTeams('tournament', value);
      } else {
        // Cleared tournament
        if (sportLockedBy === 'tournament') setSportLockedBy(null);
        setEntityTeams(null);
      }
    }

    // Auto-select sport from league
    if (name === 'league_id') {
      if (value) {
        const l = contextData.leagues.find(x => String(x.id) === String(value));
        if (l?.sport_id) {
          applySelectedSport(l.sport_id);
          setSportLockedBy('league');
        } else {
          setSportLockedBy(null);
        }
        loadEntityTeams('league', value);
      } else {
        // Cleared league
        if (sportLockedBy === 'league') setSportLockedBy(null);
        setEntityTeams(null);
      }
    }

    // Manual sport selection
    if (name === 'sport_id' && value) {
      setSportLockedBy(null);
      applySelectedSport(value);
    }
  };

  const fetchSportConfig = async (sportId) => {
    try {
      const response = await getSportWithConfig(sportId);
      setSportConfig(response.data);
    } catch (err) {
      console.warn('Failed to load sport configuration:', err);
      setSportConfig(null);
    }
  };

  const fetchSportConfigFromCatalog = async (sportId) => {
    try {
      const API_BASE_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:5000/api";
      const response = await fetch(`${API_BASE_URL}/sports-catalog/${sportId}`);
      const data = await response.json();
      
      if (data.success && data.data && data.data.sport) {
        const sport = data.data.sport;
        
        setSportConfig(sport);
        
        // Sport-specific configuration mapping: start from the sport's own
        // defaults and only take catalog values that are actually present.
        const sr = sport.scoring_rules || {};
        const gr = sport.game_rules || {};
        let catalogRules = {};

        if (sportId.includes('volleyball')) {
          catalogRules = {
            points_to_win: sr.points_to_win_set,
            total_games: sr.total_sets,
            games_to_win: sr.sets_to_win,
          };
        } else if (sportId.includes('badminton')) {
          catalogRules = {
            points_to_win: sr.points_to_win,
            win_by_margin: sr.win_by_margin,
            max_points: sr.max_points,
            total_games: sr.total_games,
            games_to_win: sr.games_to_win,
          };
        } else if (sportId.includes('basketball')) {
          // Timed game: periods are saved as periods/period_minutes, not total_games
          catalogRules = {
            periods: gr.quarters ?? gr.periods,
            period_minutes: gr.quarter_minutes ?? gr.period_minutes,
            overtime_minutes: gr.overtime_minutes,
          };
        } else {
          catalogRules = {
            points_to_win: sr.points_to_win ?? sr.pointsToWin,
            win_by_margin: sr.win_by_margin ?? (sr.win_by_two ? 2 : undefined),
            max_points: sr.max_points ?? sr.maxPoints,
            total_games: sr.total_games ?? (sr.game_format === 'best_of_3' ? 3 : sr.game_format === 'best_of_5' ? 5 : sr.game_format === 'best_of_7' ? 7 : undefined),
            games_to_win: sr.games_to_win_match ?? sr.gamesToWinMatch,
          };
        }

        applyDefaultMatchConfig(buildDefaultMatchConfig(sportId, catalogRules));
      } else {
        console.warn('No data in catalog response for:', sportId);
        // Fallback config so the panel still renders with default options
        setSportConfig({
          sportId,
          name: sportId,
          displayName: sportId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          teamType: sportId.includes('singles') || sportId.includes('individual') ? 'individual' : 'team',
          scoring_rules: {},
          game_rules: {},
        });
        applyDefaultMatchConfig(buildDefaultMatchConfig(sportId));
      }
    } catch (err) {
      console.warn('Failed to load sport catalog configuration, using local defaults:', sportId);
      // Fallback: build a minimal sportConfig from local defaults so the config panel renders
      setSportConfig({
        sportId,
        name: sportId,
        displayName: sportId.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        teamType: sportId.includes('singles') || sportId.includes('individual') ? 'individual' : 'team',
        scoring_rules: {},
        game_rules: {},
      });
      applyDefaultMatchConfig(buildDefaultMatchConfig(sportId));
    }
  };

  const handleBadmintonTypeToggle = (type) => {
    setBadmintonMatchType(type);
    const isDoubles = type === 'doubles';
    setIsDoublesMatch(isDoubles);
    
    // Badminton has separate singles/doubles catalog entries; table tennis
    // uses the same game rules for both, so its config is kept as is.
    if (getSportConfigKey(selectedSport?.name) === 'badminton') {
      const sportId = isDoubles ? 'badminton_doubles' : 'badminton_singles';
      fetchSportConfigFromCatalog(sportId);
    }
    
    // Don't change the sport_id in formData - keep it as badminton
    // Only reset participants
    setFormData(prev => ({
      ...prev,
      participant_1_id: '',
      participant_2_id: '',
      team_a_player_1_id: '',
      team_a_player_2_id: '',
      team_b_player_1_id: '',
      team_b_player_2_id: ''
    }));
    
    // Reset selected players
    setSelectedParticipant1(null);
    setSelectedParticipant2(null);
    setSelectedPlayerA1(null);
    setSelectedPlayerA2(null);
    setSelectedPlayerB1(null);
    setSelectedPlayerB2(null);
  };

  // force_create is honoured only for admins and the league / tournament host,
  // so nobody else is offered "Create Anyway".
  const canForceCreate = canForceCreateMatch(user, {
    tournament: tournaments.find((t) => String(t.id) === String(formData.tournament_id)) || null,
    league: leagues.find((l) => String(l.id) === String(formData.league_id)) || null,
  });

  const handleForceCreate = async () => {
    if (!pendingPayload || !canForceCreate) return;
    setConflictData(null);
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.post('/matches', { ...pendingPayload, force_create: true });
      setSuccess(
        response.data?.challenge_status === 'pending'
          ? "⚔️ Match challenge sent! Waiting for the opposing team to accept."
          : "✅ Match created successfully!"
      );
      setTimeout(() => {
        navigate(`/matches/${eid(response.data?.data) || eid(response.data) || response.data?.data?.id || response.data?.id}`);
      }, 2000);
    } catch (err) {
      // Re-show the conflict modal if it was the source of force-create so the
      // user still has the "Revise Schedule" and "View Existing Match" options.
      // Show the underlying error inline inside the modal.
      if (conflictData === null && pendingPayload) {
        // conflict modal was closed before this error, show plain error
        setError(err.response?.data?.message || err.message || "Failed to create match");
      } else {
        setError(err.response?.data?.message || err.message || "Failed to create match");
      }
    } finally {
      setLoading(false);
      // Don't clear pendingPayload so the modal can still try again if needed
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(null);
    setConflictData(null);

    try {
      // Prepare match data (strip UI-only fields not sent to API)
      const { _team2_name, ...matchData } = { ...formData };

      // Validate based on sport type
      if (!matchData.sport_id) {
        throw new Error("Please select a sport");
      }

      if (!matchData.venue || !matchData.venue.trim()) {
        throw new Error("Venue is required — players need to know where the match is");
      }

      if (isDoublesMatch) {
        // Validate doubles (badminton / table tennis) - need 4 players
        if (!matchData.team_a_player_1_id || !matchData.team_a_player_2_id ||
            !matchData.team_b_player_1_id || !matchData.team_b_player_2_id) {
          throw new Error(`Please select all 4 players for ${doublesSportLabel.toLowerCase()} doubles`);
        }

        // For badminton doubles, map first player of each pair to participant_1_id and participant_2_id
        // This satisfies backend validation while preserving all 4 player IDs
        matchData.participant_1_id = matchData.team_a_player_1_id;
        matchData.participant_2_id = matchData.team_b_player_1_id;

        // Also send complete doubles structure
        const doublesPairs = {
          pair_a: {
            player_1_id: matchData.team_a_player_1_id,
            player_2_id: matchData.team_a_player_2_id
          },
          pair_b: {
            player_1_id: matchData.team_b_player_1_id,
            player_2_id: matchData.team_b_player_2_id
          }
        };
        if (getSportConfigKey(selectedSport?.name) === 'tabletennis') {
          matchData.table_tennis_doubles = doublesPairs;
        } else {
          matchData.badminton_doubles = doublesPairs;
        }
      } else {
        // Regular individual or team sport
        if (!matchData.participant_1_id || !matchData.participant_2_id) {
          throw new Error("Please select both participants");
        }

        if (parseInt(matchData.participant_1_id) === parseInt(matchData.participant_2_id)) {
          throw new Error("Please select different teams to create a match");
        }
      }

      // Include match configuration if customized
      if (sportConfig && matchConfig) {
        const isBasketball = getSportConfigKey(selectedSport?.name) === 'basketball';
        matchData.match_config = {
          sport_variant: sportConfig.sportId || sportConfig.sport_id,
          scoring_rules: {
            points_to_win: matchConfig.points_to_win,
            win_by_margin: matchConfig.win_by_margin,
            max_points: matchConfig.max_points,
            // Basketball is timed: the board reads periods / period_minutes
            ...(isBasketball ? {
              periods: matchConfig.periods ?? 4,
              period_minutes: matchConfig.period_minutes ?? 10,
              overtime_minutes: matchConfig.overtime_minutes ?? 5,
            } : {}),
          },
          game_rules: {
            total_games: matchConfig.total_games,
            ...(isDoublesMatch ? { doubles: true, format: 'doubles' } : {}),
          },
          // Compared with this sport's own defaults, not badminton's (MT-B26)
          is_customized: isMatchConfigCustomized(matchConfig, defaultMatchConfig)
        };
      }

      // Derive match_type from context for the backend
      matchData.match_type = matchData.tournament_id
        ? "tournament"
        : matchData.league_id
          ? "league"
          : matchData.match_category || "friendly";
      delete matchData.match_category;

      // Strip internal UI-only field before sending to API
      delete matchData._team2_name;

      // Normalise is_private — tournament/league matches are always public
      matchData.is_private = (matchData.tournament_id || matchData.league_id)
        ? false
        : matchData.is_private === true;

      // datetime-local is the wall time at the venue (no offset): the server
      // reads it in the venue's zone, this device's zone without a map pin
      matchData.timezone = viewerTimeZone();

      setPendingPayload(matchData);
      const response = await apiClient.post('/matches', matchData);

      setSuccess(
        response.data?.challenge_status === 'pending'
          ? "⚔️ Match challenge sent! Waiting for the opposing team to accept."
          : "✅ Match created successfully!"
      );
      setTimeout(() => {
        navigate(`/matches/${eid(response.data?.data) || eid(response.data) || response.data?.data?.id || response.data?.id}`);
      }, 2000);
    } catch (err) {
      console.error('Match creation error:', err);
      const responseData = err.response?.data;
      if (err.response?.status === 409 && responseData?.conflict) {
        // Schedule conflict (the backend checks a per-sport match-length window)
        setConflictData(responseData);
      } else {
        setError(responseData?.message || err.message || "Failed to create match");
      }
    } finally {
      setLoading(false);
    }
  };

  // Derive teams for the participant dropdowns.
  // Priority: entity-scoped teams (league/tournament) > sport-filtered > all
  const filteredTeams = entityTeams !== null
    ? entityTeams
    : selectedSport
      ? teams.filter(
          (t) =>
            t.sport_type &&
            t.sport_type.toLowerCase() === selectedSport.name.toLowerCase()
        )
      : teams;

  return (
    <GameLayout
      token={token}
      onLogout={() => {}}
      profile={user}
      showFloatingNav={true}
      pageTitle="Sportifide"
    >
      {/* ── Schedule Conflict Modal ─────────────────────────────────────── */}
      {conflictData && (
        <div className="conflict-modal-overlay" onClick={() => setConflictData(null)}>
          <div className="conflict-modal" onClick={(e) => e.stopPropagation()}>
            <div className="conflict-modal-header">
              <span className="conflict-icon">⚠️</span>
              <h3>
                {conflictData.conflict?.type === "exact_duplicate"
                  ? "Duplicate Match Detected"
                  : "Schedule Conflict Detected"}
              </h3>
            </div>
            <div className="conflict-modal-body">
              <p className="conflict-message">{conflictData.message}</p>
              <div className="conflict-existing-match">
                <div className="conflict-match-name">
                  🏆 {conflictData.conflict?.match_name}
                </div>
                <div className="conflict-match-time">
                  🕐 {conflictData.conflict?.scheduled_at
                    ? formatEventDateTime(conflictData.conflict.scheduled_at, eventTimeZone(conflictData.conflict))
                    : "—"}
                </div>
              </div>
            </div>
            <div className="conflict-modal-actions">
              <button
                className="conflict-btn conflict-btn-view"
                onClick={() => navigate(`/matches/${conflictData.conflict?.match_id}`)}
              >
                👁️ View Existing Match
              </button>
              <button
                className="conflict-btn conflict-btn-revise"
                onClick={() => setConflictData(null)}
              >
                📅 Revise Schedule
              </button>
              {canForceCreate && (
                <button
                  className="conflict-btn conflict-btn-force"
                  onClick={handleForceCreate}
                >
                  ⚡ Create Anyway
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Category Coming Soon Modal ────────────────────────────────────── */}
      {showCategoryComingSoon && (
        <div className="conflict-modal-overlay" onClick={() => setShowCategoryComingSoon(false)}>
          <div className="conflict-modal" onClick={(e) => e.stopPropagation()}>
            <div className="conflict-modal-header">
              <span className="conflict-icon">🚀</span>
              <h3>Coming Soon</h3>
            </div>
            <div className="conflict-modal-body">
              <p className="conflict-message">
                <strong>Training</strong> and <strong>Ranked</strong> match categories are currently in development
                and will be available in a future update.
              </p>
              <p style={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.88rem', marginTop: '0.5rem' }}>
                For now, use <strong>Friendly</strong> for casual play or <strong>Competitive</strong>
                for serious matches.
              </p>
            </div>
            <div className="conflict-modal-actions">
              <button className="conflict-btn conflict-btn-revise" onClick={() => setShowCategoryComingSoon(false)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="match-creation-gaming">
        <FloatingCard className="header-card-gaming header-card-compact">
          <div className="header-row">
            <h2 className="match-title-large">🎯 Create New Match</h2>
            <p className="match-subtitle-right">Set up an exciting match between competitors</p>
          </div>
        </FloatingCard>

        {error && (
          <FloatingCard className="alert-card alert-error-gaming">
            <div className="alert-content">
              <span className="alert-icon">❌</span>
              <div className="alert-message">{error}</div>
            </div>
          </FloatingCard>
        )}

        {success && (
          <FloatingCard className="alert-card alert-success-gaming">
            <div className="alert-content">
              <span className="alert-icon">✅</span>
              <div className="alert-message">{success}</div>
            </div>
          </FloatingCard>
        )}

        <form onSubmit={handleSubmit} className="match-form-gaming">

          {/* Row 1: Match Details + Match Configuration side by side */}
          <div className="form-cards-sidebyside">

          {/* Match Details */}
          <FloatingCard className="form-section-gaming">
            <h3 className="form-section-title">📋 Match Details</h3>

            {/* Single 4-column grid: name spans all 4, dropdowns fill one row */}
            <div className="form-row-details">
              {/* Match Name — full width, optional (auto-generated if blank) */}
              <div className="form-group-gaming details-name">
                <label htmlFor="match_name" className="form-label-gaming">
                  Match Name
                  <span className="form-label-optional"> (optional)</span>
                </label>
                <input
                  type="text"
                  id="match_name"
                  name="match_name"
                  value={formData.match_name}
                  onChange={handleInputChange}
                  className="form-input-gaming"
                  placeholder={'Match Name (e.g. "Team A vs Team B", optional)'}
                  maxLength="255"
                />
              </div>

              {/* Tournament */}
              <div className="form-group-gaming">
                <label htmlFor="tournament_id" className="form-label-gaming">Tournament</label>
                <select
                  id="tournament_id"
                  name="tournament_id"
                  value={formData.tournament_id}
                  onChange={handleInputChange}
                  className="form-select-gaming"
                  disabled={!!formData.league_id}
                >
                  <option value="">— Optional —</option>
                  {Array.isArray(tournaments) && tournaments.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}{t.status === 'seeding' ? ' [seeding]' : t.status === 'in_progress' ? ' [in progress]' : ''}
                    </option>
                  ))}
                </select>
                {formData.league_id && (
                  <div className="form-hint form-hint-locked"><span className="hint-icon">🔒</span> League selected</div>
                )}
              </div>

              {/* League */}
              <div className="form-group-gaming">
                <label htmlFor="league_id" className="form-label-gaming">League</label>
                <select
                  id="league_id"
                  name="league_id"
                  value={formData.league_id}
                  onChange={handleInputChange}
                  className="form-select-gaming"
                  disabled={!!formData.tournament_id}
                >
                  <option value="">— Optional —</option>
                  {Array.isArray(leagues) && leagues.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}{l.status === 'in_progress' ? ' [in progress]' : l.status === 'active' ? ' [active]' : ''}
                    </option>
                  ))}
                </select>
                {formData.tournament_id && (
                  <div className="form-hint form-hint-locked"><span className="hint-icon">🔒</span> Tournament selected</div>
                )}
              </div>

              {/* Category (standalone only) */}
              <div className="form-group-gaming">
                <label htmlFor="match_category" className="form-label-gaming">Category</label>
                <select
                  id="match_category"
                  name="match_category"
                  value={formData.match_category}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'training' || val === 'ranked') {
                      setShowCategoryComingSoon(true);
                      return; // don't update formData
                    }
                    handleInputChange(e);
                  }}
                  className="form-select-gaming"
                  disabled={!!(formData.tournament_id || formData.league_id)}
                >
                  <option value="">— Optional —</option>
                  <option value="friendly">🤝 Friendly</option>
                  <option value="competitive">🏆 Competitive</option>
                  <option value="training" style={{color:'rgba(255,255,255,0.4)'}}>🎯 Training (Coming Soon)</option>
                  <option value="ranked" style={{color:'rgba(255,255,255,0.4)'}}>⭐ Ranked (Coming Soon)</option>
                </select>
                {!!(formData.tournament_id || formData.league_id) && (
                  <div className="form-hint form-hint-locked"><span className="hint-icon">🔒</span> Set by context</div>
                )}
              </div>

              {/* Sport */}
              <div className="form-group-gaming">
                <label htmlFor="sport_id" className="form-label-gaming">
                  Sport {sportLockedBy ? <span style={{fontWeight:'normal',fontSize:'0.75rem',opacity:0.7}}>(auto)</span> : '*'}
                </label>
                <select
                  id="sport_id"
                  name="sport_id"
                  value={formData.sport_id}
                  onChange={handleInputChange}
                  required={!sportLockedBy}
                  className="form-select-gaming"
                  disabled={loading || !!sportLockedBy}
                >
                  <option value="">{loading ? 'Loading…' : sports.length === 0 ? 'No sports' : 'Select Sport'}</option>
                  {Array.isArray(sports) && sports.map((sport) => (
                    <option key={sport.id} value={sport.id}>{sport.name || `Sport ${sport.id}`}</option>
                  ))}
                </select>
                {!sportLockedBy && sports.length === 0 && !loading && (
                  <div className="form-hint form-hint-locked">
                    <span className="hint-icon">💡</span>
                    <Link to="/sports" className="hint-link-bright">Add sports in My Sports</Link>
                  </div>
                )}
                {!sportLockedBy && sports.length > 0 && (
                  <div className="form-hint">
                    <span className="hint-icon">🏅</span>
                    From your profile &amp; teams · <Link to="/sports" className="hint-link">Add more</Link>
                  </div>
                )}
                {sportLockedBy && (
                  <div className="form-hint form-hint-locked"><span className="hint-icon">🔒</span> Set by {sportLockedBy}</div>
                )}
              </div>

              {/* Singles / Doubles toggle (badminton, table tennis) */}
              {isDoublesCapableSport && (
                <div className="form-group-gaming">
                  <label className="form-label-gaming">Match Type</label>
                  <div className="badminton-type-toggle">
                    <button
                      type="button"
                      className={`toggle-btn ${badmintonMatchType === 'singles' ? 'active' : ''}`}
                      onClick={() => handleBadmintonTypeToggle('singles')}
                    >
                      👤 Singles (1v1)
                    </button>
                    <button
                      type="button"
                      className={`toggle-btn ${badmintonMatchType === 'doubles' ? 'active' : ''}`}
                      onClick={() => handleBadmintonTypeToggle('doubles')}
                    >
                      👥 Doubles (2v2)
                    </button>
                  </div>
                </div>
              )}
            </div>
          </FloatingCard>

          {/* Match Configuration — always shown; placeholder until sport selected */}
          <FloatingCard className="form-section-gaming">
            {!sportConfig ? (
              <>
                <h3 className="form-section-title">🏸 Match Configuration</h3>
                <div className="form-info-message">
                  <span className="info-icon">ℹ️</span>
                  <span>Select a sport to configure match rules</span>
                </div>
              </>
            ) : (
              <>
                <div className="config-header-row">
                  <h3 className="form-section-title" style={{margin: 0}}>🏸 Match Configuration</h3>
                  <span className="config-sport-tag">{sportConfig.displayName || sportConfig.name}</span>
                </div>

                {/* All 4 config selects in one row */}
                <div className="form-row-config">
                  <div className="form-group-gaming">
                    <label htmlFor="points_to_win" className="form-label-gaming">Points to Win</label>
                    <select
                      id="points_to_win"
                      value={matchConfig.points_to_win ?? ''}
                      onChange={(e) => setMatchConfig(prev => ({ ...prev, points_to_win: parseConfigValue(e.target.value) }))}
                      className="form-select-gaming"
                    >
                      {getCurrentSportOptionsLocal().points_to_win.map(option => (
                        <option key={option.value || 'null'} value={option.value || ''}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group-gaming">
                    <label htmlFor="win_by_margin" className="form-label-gaming">Win by Margin</label>
                    <select
                      id="win_by_margin"
                      value={matchConfig.win_by_margin ?? ''}
                      onChange={(e) => setMatchConfig(prev => ({ ...prev, win_by_margin: parseConfigValue(e.target.value) }))}
                      className="form-select-gaming"
                    >
                      {getCurrentSportOptionsLocal().win_by_margin.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group-gaming">
                    <label htmlFor="max_points" className="form-label-gaming">Max Points</label>
                    <select
                      id="max_points"
                      value={matchConfig.max_points ?? ''}
                      onChange={(e) => setMatchConfig(prev => ({ ...prev, max_points: parseConfigValue(e.target.value) }))}
                      className="form-select-gaming"
                    >
                      {getCurrentSportOptionsLocal().max_points.map(option => (
                        <option key={option.value || 'null'} value={option.value || ''}>{option.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group-gaming">
                    <label htmlFor="total_games" className="form-label-gaming">Match Format</label>
                    <select
                      id="total_games"
                      value={matchConfig.total_games ?? ''}
                      onChange={(e) => setMatchConfig(prev => ({ ...prev, total_games: parseConfigValue(e.target.value) }))}
                      className="form-select-gaming"
                    >
                      {getCurrentSportOptionsLocal().total_games.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Basketball: quarter length (FIBA 10 min / NBA 12 min) */}
                {getCurrentSportOptionsLocal().period_minutes && (
                  <div className="form-row-config">
                    <div className="form-group-gaming">
                      <label htmlFor="period_minutes" className="form-label-gaming">Quarters</label>
                      <select
                        id="period_minutes"
                        value={matchConfig.period_minutes ?? 10}
                        onChange={(e) => setMatchConfig(prev => ({ ...prev, period_minutes: parseConfigValue(e.target.value) }))}
                        className="form-select-gaming"
                      >
                        {getCurrentSportOptionsLocal().period_minutes.map(option => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* One-line summary */}
                <div className="config-summary-inline">
                  <span className="config-summary-icon">📋</span>
                  {matchConfig.periods
                    ? <>{matchConfig.periods} × <strong>{matchConfig.period_minutes}</strong> min · {matchConfig.overtime_minutes} min overtime</>
                    : <>Best of <strong>{matchConfig.total_games}</strong>{matchConfig.points_to_win ? ` · ${matchConfig.points_to_win} pts to win` : ''}</>}
                  {matchConfig.win_by_margin > 0 && !matchConfig.periods && ` · win by ${matchConfig.win_by_margin}`}
                  {matchConfig.max_points && matchConfig.max_points < 999 && ` · max ${matchConfig.max_points}`}
                </div>
              </>
            )}
          </FloatingCard>

          </div>{/* end row 1: Match Details + Config */}

          {/* Row 2: Teams + Schedule side by side */}
          <div className="form-cards-sidebyside">

          {/* Participants */}
          <FloatingCard className="form-section-gaming">
            <h3 className="form-section-title">
              {isDoublesMatch ? `${doublesSportLabel === 'Table Tennis' ? '🏓' : '🏸'} Doubles Players` : isTeamSport ? '🏟️ Teams' : '👥 Participants'}
            </h3>
            
            {!selectedSport ? (
              <div className="form-info-message">
                <span className="info-icon">ℹ️</span>
                <span>Please select a sport first to choose participants</span>
              </div>
            ) : isDoublesMatch ? (
              <>
                {/* Badminton Doubles - 4 Individual Players */}
                <div className="sport-info-badge">
                  <span className="sport-badge">
                    {doublesSportLabel === 'Table Tennis' ? '🏓' : '🏸'} {doublesSportLabel} Doubles - Select 4 Players (2 Pairs)
                  </span>
                </div>

                {/* Pair A */}
                <div className="badminton-pair-section">
                  <h4 className="pair-title">Pair A</h4>
                  <div className="form-row-gaming">
                    <div className="form-group-gaming">
                      <UserSearchInput
                        label="Player A1 *"
                        placeholder="Search for Player A1..."
                        selectedUser={selectedPlayerA1}
                        onSelect={(user) => {
                          setSelectedPlayerA1(user);
                          setFormData(prev => ({ ...prev, team_a_player_1_id: user.id }));
                        }}
                        onClear={() => {
                          setSelectedPlayerA1(null);
                          setFormData(prev => ({ ...prev, team_a_player_1_id: '' }));
                        }}
                        excludeUserIds={[]}
                      />
                    </div>

                    <div className="form-group-gaming">
                      <UserSearchInput
                        label="Player A2 *"
                        placeholder="Search for Player A2..."
                        selectedUser={selectedPlayerA2}
                        onSelect={(user) => {
                          setSelectedPlayerA2(user);
                          setFormData(prev => ({ ...prev, team_a_player_2_id: user.id }));
                        }}
                        onClear={() => {
                          setSelectedPlayerA2(null);
                          setFormData(prev => ({ ...prev, team_a_player_2_id: '' }));
                        }}
                        excludeUserIds={selectedPlayerA1 ? [selectedPlayerA1.id] : []}
                      />
                    </div>
                  </div>
                </div>

                {/* Pair B */}
                <div className="badminton-pair-section">
                  <h4 className="pair-title">Pair B</h4>
                  <div className="form-row-gaming">
                    <div className="form-group-gaming">
                      <UserSearchInput
                        label="Player B1 *"
                        placeholder="Search for Player B1..."
                        selectedUser={selectedPlayerB1}
                        onSelect={(user) => {
                          setSelectedPlayerB1(user);
                          setFormData(prev => ({ ...prev, team_b_player_1_id: user.id }));
                        }}
                        onClear={() => {
                          setSelectedPlayerB1(null);
                          setFormData(prev => ({ ...prev, team_b_player_1_id: '' }));
                        }}
                        excludeUserIds={[
                          selectedPlayerA1?.id,
                          selectedPlayerA2?.id
                        ].filter(Boolean)}
                      />
                    </div>

                    <div className="form-group-gaming">
                      <UserSearchInput
                        label="Player B2 *"
                        placeholder="Search for Player B2..."
                        selectedUser={selectedPlayerB2}
                        onSelect={(user) => {
                          setSelectedPlayerB2(user);
                          setFormData(prev => ({ ...prev, team_b_player_2_id: user.id }));
                        }}
                        onClear={() => {
                          setSelectedPlayerB2(null);
                          setFormData(prev => ({ ...prev, team_b_player_2_id: '' }));
                        }}
                        excludeUserIds={[
                          selectedPlayerA1?.id,
                          selectedPlayerA2?.id,
                          selectedPlayerB1?.id
                        ].filter(Boolean)}
                      />
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* Regular Team or Individual Sport */}
                <div className="sport-info-badge">
                  <span className="sport-badge">
                    {selectedSport.name} - {isTeamSport ? 'Team Sport' : 'Individual Sport'}
                  </span>
                </div>

                <div className="form-row-gaming">
                  {isTeamSport ? (
                    <>
                      {/* Team Selection - Keep as dropdown */}
                      <div className="form-group-gaming">
                        <label htmlFor="participant_1" className="form-label-gaming">
                          Team 1 *
                        </label>
                        <select
                          id="participant_1"
                          name="participant_1_id"
                          value={formData.participant_1_id}
                          onChange={handleInputChange}
                          required
                          className="form-select-gaming"
                        >
                          <option value="">Select Team 1</option>
                          {filteredTeams.map((team) => (
                            <option key={team.id} value={team.id}>
                              {team.name}
                            </option>
                          ))}
                        </select>
                        {filteredTeams.length === 0 && (
                          <div className="form-hint">
                            <span className="hint-icon">⚠️</span>
                            {selectedSport
                              ? `No ${selectedSport.name} teams found. Create or join a ${selectedSport.name} team first.`
                              : 'No teams found. Join or create a team first.'}
                          </div>
                        )}
                      </div>

                      <div className="form-group-gaming">
                        <label className="form-label-gaming">
                          Team 2 *
                        </label>
                        {entityTeams !== null ? (
                          /* Entity-scoped: show only teams in the selected league/tournament */
                          <select
                            name="participant_2_id"
                            value={formData.participant_2_id}
                            onChange={handleInputChange}
                            required
                            className="form-select-gaming"
                            disabled={!formData.participant_1_id}
                          >
                            <option value="">Select Team 2</option>
                            {filteredTeams
                              .filter((t) => String(t.id) !== String(formData.participant_1_id))
                              .map((team) => (
                                <option key={team.id} value={team.id}>
                                  {team.name}
                                </option>
                              ))}
                          </select>
                        ) : formData.participant_1_id ? (
                          <TeamSearchInput
                            sportName={selectedSport?.name}
                            team1City={
                              teams.find(
                                (t) => t.id === parseInt(formData.participant_1_id)
                              )?.home_city || null
                            }
                            excludeTeamId={parseInt(formData.participant_1_id)}
                            value={
                              formData.participant_2_id
                                ? { id: parseInt(formData.participant_2_id), name: formData._team2_name }
                                : null
                            }
                            onChange={(team) =>
                              setFormData((prev) => ({
                                ...prev,
                                participant_2_id: team?.id || "",
                                _team2_name: team?.name || "",
                              }))
                            }
                            required
                          />
                        ) : (
                          <div className="form-hint" style={{ padding: '0.6rem 0.75rem', opacity: 0.7 }}>
                            <span className="hint-icon">ℹ️</span>
                            Select Team 1 first
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Individual Player Selection - Use Search */}
                      <div className="form-group-gaming">
                        <UserSearchInput
                          label="Participant 1 *"
                          placeholder="Search for participant 1..."
                          selectedUser={selectedParticipant1}
                          onSelect={(user) => {
                            setSelectedParticipant1(user);
                            setFormData(prev => ({ ...prev, participant_1_id: user.id }));
                          }}
                          onClear={() => {
                            setSelectedParticipant1(null);
                            setFormData(prev => ({ ...prev, participant_1_id: '' }));
                          }}
                          excludeUserIds={[]}
                        />
                      </div>

                      <div className="form-group-gaming">
                        <UserSearchInput
                          label="Participant 2 *"
                          placeholder="Search for participant 2..."
                          selectedUser={selectedParticipant2}
                          onSelect={(user) => {
                            setSelectedParticipant2(user);
                            setFormData(prev => ({ ...prev, participant_2_id: user.id }));
                          }}
                          onClear={() => {
                            setSelectedParticipant2(null);
                            setFormData(prev => ({ ...prev, participant_2_id: '' }));
                          }}
                          excludeUserIds={selectedParticipant1 ? [selectedParticipant1.id] : []}
                        />
                      </div>
                    </>
                  )}
                </div>

{/* Match Type Info removed — shown in section title + config summary */}
              </>
            )}
          </FloatingCard>

          {/* Schedule & Venue + Notes */}
          <FloatingCard className="form-section-gaming">
            <h3 className="form-section-title">📅 Schedule & Venue</h3>

            {/* Row 1: Date & Time (required) + Privacy toggle */}
            <div className="form-row-gaming" style={{ gridTemplateColumns: "1fr 1fr" }}>
              <div className="form-group-gaming">
                <label htmlFor="scheduled_at" className="form-label-gaming">Date & Time *</label>
                <input
                  type="datetime-local"
                  id="scheduled_at"
                  name="scheduled_at"
                  value={formData.scheduled_at}
                  onChange={handleInputChange}
                  required
                  className="form-input-gaming"
                  min={localDateTimeInputValue()}
                />
                <div className="form-hint">⏰ Must be at least 5 min from now</div>
              </div>

              {/* Privacy toggle */}
              <div className="form-group-gaming">
                <label className="form-label-gaming">Visibility</label>
                {(formData.tournament_id || formData.league_id) ? (
                  <>
                    <div className="match-privacy-toggle">
                      <button type="button" className="privacy-btn privacy-btn--active" disabled>
                        🌍 Public
                      </button>
                      <button type="button" className="privacy-btn" disabled style={{ opacity: 0.35 }}>
                        🔒 Private
                      </button>
                    </div>
                    <div className="form-hint">🔒 Tournament/League matches are always public</div>
                  </>
                ) : (
                  <>
                    <div className="match-privacy-toggle">
                      <button
                        type="button"
                        className={`privacy-btn${!formData.is_private ? ' privacy-btn--active' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, is_private: false }))}
                      >
                        🌍 Public
                      </button>
                      <button
                        type="button"
                        className={`privacy-btn${formData.is_private ? ' privacy-btn--active' : ''}`}
                        onClick={() => setFormData(prev => ({ ...prev, is_private: true }))}
                      >
                        🔒 Private
                      </button>
                    </div>
                    <div className="form-hint">
                      {formData.is_private
                        ? '🔒 Hidden from the Matches feed'
                        : '🌍 Visible on the Matches feed'}
                    </div>
                  </>
                )}
              </div>

              {/* Skill Level */}
              <div className="form-group-gaming">
                <label className="form-label-gaming">🏅 Skill Level</label>
                <select
                  className="form-select-gaming"
                  value={formData.skill_level || ""}
                  onChange={e => setFormData(prev => ({ ...prev, skill_level: e.target.value || null }))}
                >
                  <option value="">Any skill level</option>
                  <option value="beginner">🌿 Beginner</option>
                  <option value="recreational">🌱 Recreational</option>
                  <option value="intermediate">⚡ Intermediate</option>
                  <option value="advanced">🔥 Advanced</option>
                  <option value="professional">👑 Professional</option>
                </select>
              </div>
            </div>

            {/* Row 2: Venue + Court (full-width, side-by-side inside VenueSearch) */}
            <VenueSearch
              value={{
                venue: formData.venue,
                venue_place_id: formData.venue_place_id,
                venue_address: formData.venue_address,
                venue_lat: formData.venue_lat,
                venue_lng: formData.venue_lng,
                court: formData.court,
              }}
              onChange={(v) =>
                setFormData((prev) => ({
                  ...prev,
                  venue: v.venue ?? "",
                  venue_place_id: v.venue_place_id ?? "",
                  venue_address: v.venue_address ?? "",
                  venue_lat: v.venue_lat ?? null,
                  venue_lng: v.venue_lng ?? null,
                  court: v.court ?? "",
                }))
              }
              label="Venue"
              required
              className="form-group-gaming"
            />

            <div className="form-group-gaming" style={{marginTop: '0.5rem'}}>
              <label htmlFor="notes" className="form-label-gaming">📝 Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Additional notes or instructions (optional)"
                rows={3}
                className="form-textarea-gaming"
              />
            </div>
          </FloatingCard>

          </div>{/* end form-cards-sidebyside */}

          {/* Actions */}
          <FloatingCard className="form-actions-gaming">
            <div className="form-actions-content">
              <button
                type="button"
                onClick={() => navigate("/matches")}
                className="floating-btn cancel-btn-gaming"
                disabled={loading}
              >
                <span className="btn-icon">❌</span>
                Cancel
              </button>

              <button
                type="submit"
                className="floating-btn create-btn-gaming"
                disabled={loading}
              >
                <span className="btn-icon">
                  {loading ? "⏳" : "⚽"}
                </span>
                {loading
                  ? "Creating Match..."
                  : "Create Match"}
              </button>
            </div>
          </FloatingCard>
        </form>
      </div>
    </GameLayout>
  );
};

export default MatchCreation;
